import React, { useCallback, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { Ban, Copy, KeyRound, Plus, RotateCw } from "lucide-react";
import { Button } from "../../../../components/ui/Button";
import { Badge } from "../../../../components/ui/Badge";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { RelativeTime } from "../../../../components/ui/RelativeTime";
import { Table, TBody, TD, TH, THead, TR } from "../../../../components/ui/Table";
import { formatDate } from "../../../../utils/formatters";
import {
  createApiCredential,
  listApiCredentials,
  revokeApiCredential,
  rotateApiCredential,
} from "../../../../api/integrations";
import ConfirmCredentialActionModal from "./ConfirmCredentialActionModal";
import CreateApiCredentialModal from "./CreateApiCredentialModal";
import SecretRevealModal from "./SecretRevealModal";
import { copyToClipboard } from "../../../../utils/copyToClipboard";

/** Mismo tope que aplica el backend (ApiCredentialsService.MAX_ACTIVE_PER_COMPANY). */
export const MAX_ACTIVE_CREDENTIALS = 5;

/**
 * La sección es solo para usuarios de una empresa de comercio: las credenciales
 * cuelgan de la empresa del token. Es un filtro de UX; quien autoriza es el backend,
 * que resuelve la empresa del JWT y responde 404 ante una credencial ajena.
 */
export function canManageApiCredentials(user) {
  const raw = user?.companyId;
  const companyId = raw?._id ?? raw;
  return typeof companyId === "string" && companyId.length > 0;
}

function statusOf(result) {
  return result?.status ?? result?.error?.statusCode;
}

function actionErrorMessage(action, status, t) {
  if (status === 401 || status === 403) {
    return t("No tienes permiso para administrar credenciales de API.");
  }
  if (status === 404) return t("La credencial ya no existe. Actualiza la lista.");
  if (action === "rotate" && status === 409) {
    return t("La credencial está revocada: crea una nueva.");
  }
  return action === "revoke"
    ? t("No se pudo revocar la credencial. Intenta nuevamente.")
    : t("No se pudo rotar el secreto. Intenta nuevamente.");
}

/** Las credenciales del comercio del token (api/integrations.js). */
const COMMERCE_CREDENTIALS_API = {
  list: listApiCredentials,
  create: createApiCredential,
  rotate: rotateApiCredential,
  revoke: revokeApiCredential,
};

/**
 * «API para desarrolladores» dentro de Integraciones: listar, crear, rotar y
 * revocar las credenciales OAuth con las que el sistema del comercio crea pedidos.
 *
 * La ficha de un holding la reusa para las credenciales de la API de holdings: `api`
 * trae las cuatro llamadas (mismo contrato `{ data }` / `{ error, status }`), `intro`
 * reemplaza el texto de arriba y `embedded` omite el título propio (la ficha ya tiene uno).
 */
export default function ApiCredentialsSection({
  api = COMMERCE_CREDENTIALS_API,
  intro,
  embedded = false,
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [secret, setSecret] = useState(null);
  const [pending, setPending] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  // `silent` refresca sin volver al estado de carga: después de crear, rotar o
  // revocar la tabla no parpadea.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const result = await api.list();
    if ("error" in result) {
      setLoadFailed(true);
    } else {
      setCredentials(Array.isArray(result.data) ? result.data : []);
      setLoadFailed(false);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = credentials.filter((c) => c.status === "active").length;
  const atLimit = activeCount >= MAX_ACTIVE_CREDENTIALS;

  const handleCreated = (created) => {
    setCreateOpen(false);
    setSecret({
      clientId: created.clientId,
      clientSecret: created.clientSecret,
      reason: "created",
    });
    load({ silent: true });
  };

  const openAction = (action, credential) => {
    setActionError("");
    setPending({ action, credential });
  };

  const closeAction = () => {
    setPending(null);
    setActionError("");
  };

  const handleConfirm = async () => {
    if (!pending || actionBusy) return;
    const { action, credential } = pending;

    setActionBusy(true);
    setActionError("");
    const result =
      action === "revoke"
        ? await api.revoke(credential.clientId)
        : await api.rotate(credential.clientId);
    setActionBusy(false);

    if (!("error" in result)) {
      if (action === "rotate") {
        if (!result.data?.clientSecret) {
          setActionError(t("Respuesta inesperada del servidor"));
          return;
        }
        setSecret({
          clientId: credential.clientId,
          clientSecret: result.data.clientSecret,
          reason: "rotated",
        });
      } else {
        toast.success(t("Credencial revocada"));
      }
      closeAction();
      load({ silent: true });
      return;
    }

    const status = statusOf(result);
    if (action === "revoke" && status === 503) {
      // El backend guarda la revocación ANTES de cortar los tokens: si el corte
      // falla, la credencial igual quedó revocada y no emite tokens nuevos. Lo que
      // puede seguir funcionando es lo ya emitido, que vence solo en menos de una
      // hora. Presentarlo como error haría pensar que la revocación no ocurrió.
      toast.warning(
        t("La credencial quedó revocada, pero los tokens ya emitidos pueden seguir funcionando hasta una hora."),
      );
      closeAction();
      load({ silent: true });
      return;
    }

    setActionError(actionErrorMessage(action, status, t));
  };

  const createButton = (
    <Button
      variant="primary"
      size="sm"
      onClick={() => setCreateOpen(true)}
      disabled={atLimit}
      iconLeft={<Plus className="h-4 w-4" aria-hidden="true" />}
    >
      {t("Crear credencial")}
    </Button>
  );

  let body;
  if (loading) {
    body = (
      <p className="text-sm text-fg-muted" role="status">
        {t("Cargando credenciales…")}
      </p>
    );
  } else if (loadFailed) {
    body = (
      <div className="flex flex-wrap items-center gap-3" role="alert">
        <p className="text-sm text-status-error">{t("No pudimos cargar tus credenciales de API.")}</p>
        <Button variant="outline" size="sm" onClick={() => load()}>
          {t("Reintentar")}
        </Button>
      </div>
    );
  } else if (credentials.length === 0) {
    body = (
      <EmptyState
        icon={<KeyRound aria-hidden="true" />}
        title={t("Todavía no tienes credenciales de API")}
        description={t("Crea una para conectar tu sistema con Beartrack.")}
        action={createButton}
      />
    );
  } else {
    body = (
      <Table>
        <THead>
          <TR>
            <TH>{t("Nombre")}</TH>
            <TH>{t("Client ID")}</TH>
            <TH>{t("Estado")}</TH>
            <TH>{t("Creada")}</TH>
            <TH>{t("Último uso")}</TH>
            <TH>
              <span className="sr-only">{t("Acciones")}</span>
            </TH>
          </TR>
        </THead>
        <TBody>
          {credentials.map((credential) => {
            const active = credential.status === "active";
            return (
              <TR key={credential.clientId}>
                <TD className="font-medium">{credential.name}</TD>
                <TD>
                  <div className="flex items-center gap-1.5">
                    <code className="font-mono text-xs">{credential.clientId}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t("Copiar Client ID de {{name}}", { name: credential.name })}
                      onClick={() =>
                        copyToClipboard(credential.clientId, t("Client ID copiado"), t)
                      }
                      iconLeft={<Copy className="h-3.5 w-3.5" aria-hidden="true" />}
                    />
                  </div>
                </TD>
                <TD>
                  <Badge tone={active ? "success" : "muted"} dot>
                    {active ? t("Activa") : t("Revocada")}
                  </Badge>
                </TD>
                <TD className="whitespace-nowrap">
                  {credential.createdAt ? formatDate(credential.createdAt) : "—"}
                </TD>
                <TD className="whitespace-nowrap">
                  {credential.lastUsedAt ? (
                    <RelativeTime value={credential.lastUsedAt} t={t} />
                  ) : (
                    t("Nunca")
                  )}
                </TD>
                <TD className="whitespace-nowrap text-right">
                  {/* Una revocada no se puede rotar ni reactivar: no hay acciones. */}
                  {active && (
                    <div className="inline-flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={t("Rotar secreto de {{name}}", { name: credential.name })}
                        onClick={() => openAction("rotate", credential)}
                        iconLeft={<RotateCw className="h-3.5 w-3.5" aria-hidden="true" />}
                      >
                        {t("Rotar secreto")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-status-error"
                        aria-label={t("Revocar {{name}}", { name: credential.name })}
                        onClick={() => openAction("revoke", credential)}
                        iconLeft={<Ban className="h-3.5 w-3.5" aria-hidden="true" />}
                      >
                        {t("Revocar")}
                      </Button>
                    </div>
                  )}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    );
  }

  return (
    <section
      className={embedded ? undefined : "mig__section"}
      aria-labelledby={embedded ? undefined : titleId}
      aria-label={embedded ? t("Credenciales de API") : undefined}
    >
      {!embedded && (
        <div className="mig__section-head">
          <h2 className="mig__section-title" id={titleId}>
            {t("API para desarrolladores")}
          </h2>
        </div>
      )}

      <div className="space-y-4 rounded-lg border border-border bg-bg p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-2xl text-sm text-fg-muted">
            {intro ??
              t("Credenciales para que tu sistema (ERP, tienda propia) cree pedidos en Beartrack por API. Puedes revocarlas en cualquier momento.")}
          </p>
          {!loading && !loadFailed && credentials.length > 0 && createButton}
        </div>

        {!loading && !loadFailed && atLimit && (
          <p className="text-xs text-fg-muted" role="status">
            {t("Alcanzaste el máximo de {{max}} credenciales activas. Revoca una para crear otra.", {
              max: MAX_ACTIVE_CREDENTIALS,
            })}
          </p>
        )}

        {body}
      </div>

      <CreateApiCredentialModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        maxActive={MAX_ACTIVE_CREDENTIALS}
        createCredential={api.create}
      />
      <SecretRevealModal secret={secret} onClose={() => setSecret(null)} />
      <ConfirmCredentialActionModal
        action={pending?.action}
        credential={pending?.credential}
        busy={actionBusy}
        errorMessage={actionError}
        onConfirm={handleConfirm}
        onCancel={closeAction}
      />
    </section>
  );
}
